import React, { useEffect } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import MeshMavenError from 'screens/performanceReview/components/ContinuousFeedback/Drawers/common/MeshMaven/MeshMavenError';
import useReviewFormParams from 'screens/performanceReview/hooks/useReviewFormParams';

import { CheckCircle, CloseIcon } from 'assets/assetsV1/iconComponents';
import { ContentAnalyzerGradient } from 'assets/assetsV1/iconComponents/ContentAnalyzerGradient';

import { sendReanalyzedMeshMavenContentEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import GradientButton from 'uiToolkitV1/GradientButton/GradientButton';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import MeshMavenLoading from './MeshMavenLoading';
import { useAnalyzeTextMutation, useAnalyzeTextMutationLoading } from './dataHooks/analyzeText';
import { useRephraseTextMutationLoading } from './dataHooks/rephraseText';
import {
  useFeedbackAnalysis,
  useGroupId,
  useRevisions,
  useSelectedRevision,
  useSelectedRevisionNumber,
} from './slice/meshMavenSlice';

const getCoinMapping = ({
  revieweeDisplayName,
  isSelfReview,
}: {
  revieweeDisplayName: string;
  isSelfReview: boolean;
}) => ({
  context: `Context: what project, goal, task or situation you are giving ${
    isSelfReview ? 'yourself' : revieweeDisplayName
  } feedback on.`,
  observations: `Observation: what action or behaviour that you have observed ${
    isSelfReview ? 'yourself' : revieweeDisplayName
  } on`,
  impact: `Impact: the outcome of the action or behavior (whether positive or negative).`,
  next_steps: `Next Steps: what you would suggest are the things ${
    isSelfReview ? 'you' : revieweeDisplayName
  } should do to improve ${isSelfReview ? 'yourselves' : 'themselves'}.`,
});

const SuggestionText = ({ analysis = {}, revieweeDisplayName, isSelfReview }) => {
  const labelsPresent = Object.keys(analysis).filter((k) => analysis[k] === 1);
  const labelsNotPresent = Object.keys(analysis).filter((k) => analysis[k] === 0);
  const selectedRevision = useSelectedRevision();

  return (
    <Flex flexDir="column" py={2} px={3}>
      {selectedRevision.isReanalyzeEnabled && selectedRevision.isTextLengthValid && (
        <TextLabel bold color="alerts.in_progress" mb={2}>
          Text changes detected, click on &quot;Reanalyse&quot; for updated analysis.
        </TextLabel>
      )}
      <TextBodyMedium mb={3}>Maven analysed your text and here&apos;s what it found:</TextBodyMedium>
      {labelsPresent.length > 0 && (
        <>
          <TextLabel bold mb={1}>
            Your feedback includes:
          </TextLabel>
          <Flex flexDir="column" gap={1}>
            {labelsPresent.map((k) => (
              <Flex key={k} alignItems="flex-start" gap={2}>
                <CheckCircle mt="3px" color="functional.2F8133" />
                <TextBodyMedium>{getCoinMapping({ revieweeDisplayName, isSelfReview })[k]}</TextBodyMedium>
              </Flex>
            ))}
          </Flex>
        </>
      )}
      {labelsNotPresent.length > 0 && (
        <>
          <TextLabel bold mt={3} mb={1}>
            Your feedback could improve by including:
          </TextLabel>
          <Flex flexDir="column" gap={1}>
            {labelsNotPresent.map((k) => (
              <Flex key={k} alignItems="flex-start" gap={2}>
                <CloseIcon mt="3px" color="primary.FD812D" />
                <TextBodyMedium>{getCoinMapping({ revieweeDisplayName, isSelfReview })[k]}</TextBodyMedium>
              </Flex>
            ))}
          </Flex>
        </>
      )}
    </Flex>
  );
};

const AnalyzeText = ({ isSelfReview, questionId, reviewId, revieweeDisplayName, revieweeId }) => {
  const { mutate: analyzeText, error: analysisError } = useAnalyzeTextMutation();

  const { employeeId, providerId } = useReviewFormParams();
  const queryClient = useQueryClient();
  const sidebarData = queryClient.getQueryData([REACT_QUERY_KEYS.ReviewFormSidebar, employeeId, reviewId, providerId]);
  const { reviewInfo } = sidebarData ?? {};
  const { providerType } = reviewInfo ?? {};

  const isAnalyzeLoading = useAnalyzeTextMutationLoading();
  const isRephraseLoading = useRephraseTextMutationLoading();

  const revisions = useRevisions();
  const revision = useSelectedRevision();
  const revisionNumber = useSelectedRevisionNumber();
  const analysis = useFeedbackAnalysis();
  const groupId = useGroupId();

  useEffect(() => {
    if (!revision || revisions[revisions.length - 1].requestId) {
      return;
    }

    analyzeText({
      reviewId,
      empId: revieweeId,
      revisionNumber,
      feedbackText: revision.updatedState,
      groupId,
    });
  }, [analyzeText, questionId, reviewId, revieweeId, revisions.length]);

  if (!revision) {
    return null;
  }

  return (
    <Flex
      w={{ base: '100%', desktop: '45%' }}
      order={{ base: 1, desktop: 0 }}
      borderWidth="1px"
      borderColor="neutral.EBEFF3"
      borderRadius="8px"
      p={1}
      flexDir="column"
      bg="linear-gradient(106.11deg, rgba(0, 142, 240, 0.03) 1.41%, rgba(236, 59, 100, 0.03) 36.8%, rgba(255, 194, 0, 0.03) 69.67%, rgba(92, 196, 97, 0.03) 106.6%)"
    >
      <Flex
        justify="space-between"
        alignItems="center"
        p="6px 12px"
        borderBottomWidth="1px"
        borderBottomColor="neutral.EBEFF3"
      >
        <TextBodyMedium bold w="100%">
          Review comment analysis
        </TextBodyMedium>
        <Tooltip
          label={
            revision.updatedState.length < 15 ? 'Add more text to leverage Maven. Currently supports English only.' : ''
          }
        >
          <Box
            sx={{
              button: {
                justifyContent: 'flex-end',
              },
            }}
          >
            <GradientButton
              // @ts-ignore
              onClick={() => {
                sendReanalyzedMeshMavenContentEvent({
                  eventProperties: {
                    source: providerType,
                  },
                });
                analyzeText({
                  reviewId,
                  empId: revieweeId,
                  revisionNumber,
                  feedbackText: revision.updatedState,
                  groupId,
                });
              }}
              buttonProps={{
                StartIcon: ContentAnalyzerGradient,
              }}
              isDisabled={
                !revision.isReanalyzeEnabled || !revision.isTextLengthValid || isAnalyzeLoading || isRephraseLoading
              }
              text="Reanalyse"
            />
          </Box>
        </Tooltip>
      </Flex>
      {isAnalyzeLoading || isRephraseLoading ? (
        <Box my="auto">
          <MeshMavenLoading loadingText="Initiating a detailed analysis of the content" />
        </Box>
      ) : analysisError ? (
        <MeshMavenError errorText={analysisError?.message} />
      ) : (
        <SuggestionText analysis={analysis} revieweeDisplayName={revieweeDisplayName} isSelfReview={isSelfReview} />
      )}
    </Flex>
  );
};

export default AnalyzeText;
